import { useState } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { subscriptionApi } from '../services/api'
import { useOnboardingStatus } from '../hooks/useOnboardingStatus'
import ConfirmDialog from '../components/ConfirmDialog'
import { Settings, CreditCard, AlertTriangle, CheckCircle, Clock, XCircle, Loader2, Tag } from 'lucide-react'

export default function SettingsPage() {
  const { user } = useAuth0()
  const { subscriptionStatus, planName, trialEndsAt } = useOnboardingStatus()
  const queryClient = useQueryClient()

  const [showCancelDialog, setShowCancelDialog] = useState(false)
  const [cancelling, setCancelling] = useState(false)
  const [cancelError, setCancelError] = useState<string | null>(null)
  const [cancelSuccess, setCancelSuccess] = useState(false)

  // Discount code state
  const [discountCode, setDiscountCode] = useState('')
  const [applyingCode, setApplyingCode] = useState(false)
  const [codeError, setCodeError] = useState<string | null>(null)
  const [codeSuccess, setCodeSuccess] = useState(false)

  const { data: subscription, isLoading: loading } = useQuery({
    queryKey: ['subscription-current'],
    queryFn: () => subscriptionApi.getCurrent().then((r) => r.data),
    staleTime: 60000,
    retry: false,
  })

  const handleCancel = async () => {
    setCancelling(true)
    setCancelError(null)
    try {
      await subscriptionApi.cancel()
      setCancelSuccess(true)
      setShowCancelDialog(false)
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['onboarding-status'] }),
        queryClient.invalidateQueries({ queryKey: ['subscription-current'] }),
      ])
    } catch (err: any) {
      setCancelError(err?.response?.data?.error || 'Nie udało się anulować subskrypcji')
    } finally {
      setCancelling(false)
    }
  }

  const handleApplyDiscountCode = async () => {
    if (!discountCode.trim()) return
    setApplyingCode(true)
    setCodeError(null)
    try {
      await subscriptionApi.applyDiscountCode(discountCode.trim())
      setCodeSuccess(true)
      setDiscountCode('')
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['subscription-current'] }),
        queryClient.invalidateQueries({ queryKey: ['onboarding-status'] }),
      ])
    } catch (err: any) {
      setCodeError(err?.response?.data?.error || 'Nie udało się zastosować kodu rabatowego')
    } finally {
      setApplyingCode(false)
    }
  }

  const statusConfig: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
    Active: { label: 'Aktywna', color: 'text-green-700 bg-green-50 border-green-200', icon: <CheckCircle className="w-4 h-4 text-green-600" /> },
    Trial: { label: 'Okres próbny', color: 'text-blue-700 bg-blue-50 border-blue-200', icon: <Clock className="w-4 h-4 text-blue-600" /> },
    Expired: { label: 'Wygasła', color: 'text-red-700 bg-red-50 border-red-200', icon: <XCircle className="w-4 h-4 text-red-600" /> },
    Cancelled: { label: 'Anulowana', color: 'text-gray-700 bg-gray-50 border-gray-200', icon: <XCircle className="w-4 h-4 text-gray-500" /> },
    Suspended: { label: 'Zawieszona', color: 'text-yellow-700 bg-yellow-50 border-yellow-200', icon: <AlertTriangle className="w-4 h-4 text-yellow-600" /> },
    Pending: { label: 'Oczekująca', color: 'text-gray-700 bg-gray-50 border-gray-200', icon: <Clock className="w-4 h-4 text-gray-500" /> },
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    )
  }

  const status = subscription?.status || subscriptionStatus || 'Pending'
  const statusInfo = statusConfig[status] || statusConfig.Pending

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center gap-3 mb-8">
        <Settings className="w-6 h-6 text-gray-400" />
        <h1 className="text-2xl font-bold text-gray-900">Ustawienia konta</h1>
      </div>

      {/* Account info */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Informacje o koncie</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-gray-500">Email</p>
            <p className="text-sm font-medium text-gray-900">{user?.email || '—'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Imię i nazwisko</p>
            <p className="text-sm font-medium text-gray-900">{user?.name || '—'}</p>
          </div>
        </div>
      </div>

      {/* Subscription info */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <CreditCard className="w-5 h-5 text-gray-400" />
          <h2 className="text-lg font-semibold text-gray-900">Subskrypcja</h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          <div>
            <p className="text-sm text-gray-500">Plan</p>
            <p className="text-sm font-medium text-gray-900">
              {subscription?.planDisplayName || planName || '—'}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Status</p>
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${statusInfo.color}`}>
              {statusInfo.icon}
              {statusInfo.label}
            </span>
          </div>
          {status === 'Trial' && trialEndsAt && (
            <div>
              <p className="text-sm text-gray-500">Okres próbny kończy się</p>
              <p className="text-sm font-medium text-gray-900">
                {new Date(trialEndsAt).toLocaleDateString('pl-PL', { day: 'numeric', month: 'long', year: 'numeric' })}
              </p>
            </div>
          )}
          {subscription?.currentPeriodEnd && status === 'Active' && (
            <div>
              <p className="text-sm text-gray-500">Następne odnowienie</p>
              <p className="text-sm font-medium text-gray-900">
                {new Date(subscription.currentPeriodEnd).toLocaleDateString('pl-PL', { day: 'numeric', month: 'long', year: 'numeric' })}
              </p>
            </div>
          )}
        </div>

        {/* Usage */}
        {subscription && (
          <div className="border-t border-gray-100 pt-4 mb-6">
            <p className="text-sm font-medium text-gray-700 mb-3">Wykorzystanie w bieżącym okresie</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <UsageBar
                label="Dokumenty"
                used={subscription.documentsUsed}
                limit={subscription.documentsLimit}
              />
              <UsageBar
                label="Strony"
                used={subscription.pagesUsed}
                limit={subscription.pagesLimit}
              />
              <UsageBar
                label="Tokeny AI"
                used={subscription.tokensUsed}
                limit={subscription.tokensLimit}
              />
            </div>
          </div>
        )}

        {/* Discount code section */}
        {(status === 'Active' || status === 'Trial') && (
          <div className="border-t border-gray-100 pt-4 mb-4">
            <div className="flex items-center gap-2 mb-3">
              <Tag className="w-4 h-4 text-gray-400" />
              <p className="text-sm font-medium text-gray-700">Kod rabatowy</p>
            </div>

            {subscription?.discountCode ? (
              <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg p-3">
                <CheckCircle className="w-4 h-4" />
                Kod rabatowy <span className="font-mono font-medium">{subscription.discountCode}</span> — zastosowany
              </div>
            ) : codeSuccess ? (
              <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg p-3">
                <CheckCircle className="w-4 h-4" />
                Kod rabatowy został zastosowany
              </div>
            ) : (
              <div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={discountCode}
                    onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                    placeholder="Wpisz kod rabatowy"
                    maxLength={50}
                    className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono"
                    disabled={applyingCode}
                  />
                  <button
                    onClick={handleApplyDiscountCode}
                    disabled={applyingCode || !discountCode.trim()}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
                  >
                    {applyingCode ? 'Stosowanie...' : 'Zastosuj kod'}
                  </button>
                </div>
                {codeError && (
                  <p className="mt-2 text-sm text-red-600">{codeError}</p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Cancel success / cancelled state banner (shown regardless of current status to avoid flicker) */}
        {(cancelSuccess || status === 'Cancelled') && (
          <div className="border-t border-gray-100 pt-4">
            <div className="flex items-start gap-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg p-3">
              <CheckCircle className="w-4 h-4 mt-0.5" />
              <div>
                <p>Subskrypcja została anulowana.</p>
                {subscription?.currentPeriodEnd ? (
                  <p className="text-green-700/80">
                    Dostęp pozostaje aktywny do{' '}
                    {new Date(subscription.currentPeriodEnd).toLocaleDateString('pl-PL', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                    })}
                    .
                  </p>
                ) : (
                  <p className="text-green-700/80">
                    Dostęp będzie aktywny do końca bieżącego okresu rozliczeniowego.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Cancel button */}
        {!cancelSuccess && (status === 'Active' || status === 'Trial') && (
          <div className="border-t border-gray-100 pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-900">Rezygnacja z subskrypcji</p>
                <p className="text-xs text-gray-500">
                  Po rezygnacji zachowasz dostęp do końca opłaconego okresu.
                </p>
              </div>
              <button
                onClick={() => setShowCancelDialog(true)}
                className="px-4 py-2 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 border border-red-200 rounded-lg transition-colors"
              >
                Rezygnuj z subskrypcji
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Cancel confirmation dialog */}
      <ConfirmDialog
        isOpen={showCancelDialog}
        title="Rezygnacja z subskrypcji"
        message="Czy na pewno chcesz zrezygnować z subskrypcji? Po zakończeniu bieżącego okresu rozliczeniowego utracisz dostęp do generowania opinii, tworzenia dokumentów i innych funkcji LexTool. Twoje dane pozostaną bezpieczne."
        confirmLabel="Tak, rezygnuję"
        cancelLabel="Nie, zachowaj subskrypcję"
        variant="danger"
        isLoading={cancelling}
        loadingLabel="Anulowanie..."
        error={cancelError}
        onConfirm={handleCancel}
        onCancel={() => {
          setShowCancelDialog(false)
          setCancelError(null)
        }}
      />
    </div>
  )
}

function UsageBar({ label, used, limit }: { label: string; used: number; limit: number }) {
  const percentage = limit > 0 ? Math.min((used / limit) * 100, 100) : 0
  const isHigh = percentage > 80

  return (
    <div className="bg-gray-50 rounded-lg p-3">
      <div className="flex justify-between items-center mb-1.5">
        <span className="text-xs text-gray-500">{label}</span>
        <span className="text-xs font-medium text-gray-700">
          {used.toLocaleString('pl-PL')} / {limit.toLocaleString('pl-PL')}
        </span>
      </div>
      <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${isHigh ? 'bg-red-500' : 'bg-blue-500'}`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  )
}
